import { useId } from 'react';
import styled from 'styled-components';
import { LETTERS, MARK_BOTTOM, MARK_TOP } from './logo-paths';

const Svg = styled.svg`
  color: #8282a1;
  display: block;
  flex-shrink: 0;
  height: ${({ $height }) => $height}px;
  width: auto;

  .logo-dark {
    display: none;
  }

  .dark &[data-variant='auto'],
  &[data-variant='dark'] {
    color: #fafaff;

    .logo-light {
      display: none;
    }

    .logo-dark {
      display: inline;
    }
  }
`;

/**
 * Bidali wordmark as inline SVG. By default it follows the theme (the swap is
 * pure CSS, so there is no flash and no hydration mismatch).
 *
 *   <Logo />                 // theme-aware, 24px tall
 *   <Logo height={28} />
 *   <Logo variant="dark" />  // always the light-on-dark version
 *
 * `variant`: 'auto' | 'light' | 'dark'. Decorative by default: label the
 * surrounding link instead. Pass `title` when it stands alone.
 */
const Logo = ({ className, height = 24, title, variant = 'auto' }) => {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, '');

  return (
    <Svg
      aria-hidden={title ? undefined : 'true'}
      aria-label={title}
      className={className}
      data-variant={variant}
      $height={height}
      role={title ? 'img' : undefined}
      viewBox="0 0 93.36 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <radialGradient
          cx="31.9999025%"
          cy="93.000243%"
          fx="31.9999025%"
          fy="93.000243%"
          id={`${id}-light-top`}
          r="71.8469458%"
        >
          <stop offset="31.6574%" stopColor="#E4517F" />
          <stop offset="100%" stopColor="#4B4DF1" />
        </radialGradient>
        <linearGradient
          id={`${id}-light-bottom`}
          x1="-13.9999887%"
          x2="33.0876024%"
          y1="111.322026%"
          y2="-3.61560426%"
        >
          <stop offset="0%" stopColor="#E4517F" />
          <stop offset="62.8201%" stopColor="#E35180" />
          <stop offset="96.6418%" stopColor="#4B4DF1" />
        </linearGradient>
        <radialGradient
          cx="44.9794093%"
          cy="74.322402%"
          fx="44.9794093%"
          fy="74.322402%"
          id={`${id}-dark-top`}
          r="49.1020708%"
        >
          <stop offset="0%" stopColor="#E4517F" />
          <stop offset="100%" stopColor="#6768F5" />
        </radialGradient>
        <linearGradient
          id={`${id}-dark-bottom`}
          x1="-13.9999887%"
          x2="31.5063069%"
          y1="111.322026%"
          y2="0.244231239%"
        >
          <stop offset="0%" stopColor="#E4517F" />
          <stop offset="62.8201%" stopColor="#E35180" />
          <stop offset="100%" stopColor="#6768F5" />
        </linearGradient>
      </defs>
      <g fill="none" fillRule="evenodd">
        <path d={LETTERS} fill="currentColor" />
        <g className="logo-light">
          <path d={MARK_TOP} fill={`url(#${id}-light-top)`} />
          <path d={MARK_BOTTOM} fill={`url(#${id}-light-bottom)`} />
        </g>
        <g className="logo-dark">
          <path d={MARK_TOP} fill={`url(#${id}-dark-top)`} />
          <path d={MARK_BOTTOM} fill={`url(#${id}-dark-bottom)`} />
        </g>
      </g>
    </Svg>
  );
};

export default Logo;
